import { useEffect, useRef, useCallback } from 'react';
import { trackFormEvent } from '../services/analyticsService';
import type { PageType } from '../types/analytics';

interface UseFormTrackingOptions {
  formId: string;
  originPageType?: PageType | null;
  originSlug?: string | null;
  enabled?: boolean;
}

interface FormTrackingReturn {
  trackStart: () => void;
  startedAt: Date | null;
}

export function useFormTracking({
  formId,
  originPageType = null,
  originSlug = null,
  enabled = true,
}: UseFormTrackingOptions): FormTrackingReturn {
  const hasTrackedView = useRef(false);
  const hasTrackedStart = useRef(false);
  const startedAt = useRef<Date | null>(null);

  useEffect(() => {
    if (!enabled || !formId || hasTrackedView.current) return;

    hasTrackedView.current = true;

    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source') || params.get('source') || null;

    trackFormEvent({
      formId,
      eventType: 'view',
      originPageType,
      originSlug,
      referrer: document.referrer || null,
      utmSource,
    });
  }, [formId, originPageType, originSlug, enabled]);

  const trackStart = useCallback(() => {
    if (!enabled || !formId || hasTrackedStart.current) return;

    hasTrackedStart.current = true;
    startedAt.current = new Date();

    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source') || params.get('source') || null;

    trackFormEvent({
      formId,
      eventType: 'start',
      originPageType,
      originSlug,
      referrer: document.referrer || null,
      utmSource,
    });
  }, [formId, originPageType, originSlug, enabled]);

  return {
    trackStart,
    startedAt: startedAt.current,
  };
}
